import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import {
  filterExecutiveOptionsByQuery,
  type ExecutiveFilterOption,
} from '../../lib/executive-filters'
import styles from './ExecutiveHeader.module.css'

interface SearchableFilterFieldProps {
  id: string
  label: string
  value: string
  options: ReadonlyArray<ExecutiveFilterOption<string>>
  onChange: (value: string) => void
  searchPlaceholder: string
  emptyMessage: string
  icon?: string
}

export function SearchableFilterField({
  id,
  label,
  value,
  options,
  onChange,
  searchPlaceholder,
  emptyMessage,
  icon,
}: SearchableFilterFieldProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [listPosition, setListPosition] = useState({ top: 0, left: 0, width: 0 })
  const fieldRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const generatedId = useId()
  const listId = `${id}-list`
  const searchId = `${id}-search-${generatedId}`

  const selectedOption = options.find((option) => option.id === value)
  const filteredOptions = useMemo(
    () => filterExecutiveOptionsByQuery(options, query),
    [options, query]
  )

  const closeList = useCallback(() => {
    setIsOpen(false)
    setQuery('')
    setHighlightedIndex(0)
  }, [])

  const selectOption = useCallback(
    (optionId: string) => {
      onChange(optionId)
      closeList()
      triggerRef.current?.focus()
    },
    [closeList, onChange]
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (fieldRef.current?.contains(target) || listRef.current?.contains(target)) {
        return
      }
      closeList()
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [closeList, isOpen])

  useEffect(() => {
    if (!isOpen || !triggerRef.current) {
      return
    }

    const rect = triggerRef.current.getBoundingClientRect()
    setListPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 280),
    })
    searchRef.current?.focus()
  }, [isOpen])

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault()
      setIsOpen(true)
    }
  }

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      closeList()
      triggerRef.current?.focus()
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlightedIndex((current) =>
        filteredOptions.length === 0
          ? 0
          : Math.min(current + 1, filteredOptions.length - 1)
      )
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlightedIndex((current) => Math.max(current - 1, 0))
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      const option = filteredOptions[highlightedIndex]
      if (option) {
        selectOption(option.id)
      }
    }
  }

  const dropdown = isOpen ? (
    <div
      ref={listRef}
      className={styles.searchDropdown}
      style={{
        top: `${listPosition.top}px`,
        left: `${listPosition.left}px`,
        width: `${listPosition.width}px`,
      }}
    >
      <div className={styles.searchWrap}>
        <Icon icon="mdi:magnify" className={styles.searchIcon} aria-hidden />
        <input
          ref={searchRef}
          id={searchId}
          type="search"
          className={styles.searchInput}
          value={query}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          autoComplete="off"
          onChange={(event) => {
            setQuery(event.target.value)
            setHighlightedIndex(0)
          }}
          onKeyDown={handleSearchKeyDown}
        />
      </div>
      <div id={listId} role="listbox" aria-label={label} className={styles.searchList}>
        {filteredOptions.length === 0 ? (
          <p className={styles.searchEmpty}>{emptyMessage}</p>
        ) : (
          filteredOptions.map((option, index) => {
            const isSelected = option.id === value
            const isHighlighted = index === highlightedIndex
            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={[
                  styles.searchOption,
                  isSelected ? styles.searchOptionSelected : '',
                  isHighlighted ? styles.searchOptionHighlighted : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => selectOption(option.id)}
              >
                {option.label}
              </button>
            )
          })
        )}
      </div>
    </div>
  ) : null

  return (
    <div ref={fieldRef} className={styles.field}>
      <label htmlFor={id}>{label}</label>
      <span className={styles.control}>
        <button
          ref={triggerRef}
          id={id}
          type="button"
          role="combobox"
          className={styles.searchTrigger}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listId}
          onClick={() => setIsOpen((current) => !current)}
          onKeyDown={handleTriggerKeyDown}
        >
          {icon ? (
            <Icon icon={icon} className={styles.searchTriggerIcon} aria-hidden />
          ) : null}
          <span className={styles.searchTriggerValue}>
            {selectedOption?.label ?? options[0]?.label ?? ''}
          </span>
          <Icon
            icon="mdi:chevron-down"
            className={
              isOpen
                ? `${styles.searchChevron} ${styles.searchChevronOpen}`
                : styles.searchChevron
            }
            aria-hidden
          />
        </button>
      </span>
      {typeof document !== 'undefined' ? createPortal(dropdown, document.body) : null}
    </div>
  )
}
